import { ACCEPTED_FILE_TYPES } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import React, { useCallback, useState } from "react";

const logger = appLogger.withTag("useFileDragAndDrop");

interface UseFileDragAndDropProps {
  onFilesDrop: (files: File[]) => void;
  acceptedFileTypes?: string[];
}

interface UseFileDragAndDropReturn {
  isDragOver: boolean;
  isDragActive: boolean;
  isValidFileType: boolean;
  dragHandlers: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

const useFileDragAndDrop = ({ onFilesDrop, acceptedFileTypes = ACCEPTED_FILE_TYPES }: UseFileDragAndDropProps): UseFileDragAndDropReturn => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isValidFileType, setIsValidFileType] = useState(true);

  const validateFileType = useCallback(
    (file: File): boolean => {
      return acceptedFileTypes.includes(file.type);
    },
    [acceptedFileTypes],
  );

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set drag inactive if we're leaving the drop zone completely
    const nextTarget = e.relatedTarget as Node | null;
    if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
      setIsDragActive(false);
      setIsDragOver(false);
      setIsValidFileType(true); // Reset to default
    }
  }, []);

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Check if dragged items contain files
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        const fileItems = Array.from(e.dataTransfer.items).filter((item) => item.kind === "file");

        if (fileItems.length > 0) {
          const hasValidFiles = fileItems.some((item) => acceptedFileTypes.includes(item.type));

          setIsDragOver(true); // Always show drag over when files are present
          setIsValidFileType(hasValidFiles); // Track if files are valid
        }
      }
    },
    [acceptedFileTypes],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragActive(false);
      setIsDragOver(false);
      setIsValidFileType(true); // Reset to default

      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = droppedFiles.filter(validateFileType);

      if (validFiles.length > 0) {
        onFilesDrop(validFiles);
      } else if (droppedFiles.length > 0) {
        logger.warn(
          "Dropped files are not valid image types:",
          droppedFiles.map((f) => f.type),
        );
      }
    },
    [onFilesDrop, validateFileType],
  );

  return {
    isDragOver,
    isDragActive,
    isValidFileType,
    dragHandlers: {
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
    },
  };
};

export default useFileDragAndDrop;
